import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { Chip } from "@mui/material";
import { GridText } from "src/components/Grid/GridText";
import { useTranslation } from "react-i18next";
import { MrtColumns, extendColumnsByJsonAttributes } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { User } from "src/api/client-api/user/useAllUsers";
import { ASSIGN, ROLE, VIEW, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useRoles } from "src/api/client-api/roles/useRoles";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

type Options = {
    users?: User[];
};
export const useUserColumns = (options: Options): MrtColumns<User> => {
    const { t } = useTranslation();
    const { canUser, entitiesJsonAttributes } = useFoodpilotContext();
    const canReadRoles = canUser(VIEW, ROLE) || canUser(ASSIGN, ROLE);
    const { getRoleDisplayName } = useRoles({ enabled: canReadRoles });
    const { users } = options;

    const columnHelper = createMRTColumnHelper<User>();

    const userJsonAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.user] || [];

    const mrt_columns = [
        columnHelper.accessor((row) => row.id, {
            id: "id",
            header: t("users.id", "ID"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor((row) => `${row.firstname} ${row.lastname}`, {
            id: "lastname",
            header: t("users.name", "Name"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
            grow: false,
        }),
        columnHelper.accessor("email", {
            header: t("users.email", "Email"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor("phone", {
            header: t("users.phone", "Phone"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue || "-"} />;
            },
        }),
        columnHelper.accessor((row) => row.organizations?.map((c) => c.name).join(", ") || "-", {
            id: "organizations.name",
            header: t("users.companies", "Companies"),
            Cell: (value) => {
                return <GridText value={value.renderedCellValue} />;
            },
        }),
        columnHelper.accessor(
            (row) => {
                const roles = row.roles.filter((role) => role !== "ROLE_USER");
                return roles.length > 0 ? roles.map(getRoleDisplayName).join(", ") : "-";
            },
            {
                id: "roles",
                header: t("users.roles", "Roles"),
                Cell: (value) => {
                    return <GridText value={value.renderedCellValue} />;
                },
                enableSorting: false,
            },
        ),
        columnHelper.accessor((row) => row.is_verified || false, {
            id: "is_verified",
            header: t("users.verified", "Verified"),
            Cell: (value) => {
                const isVerified = value.renderedCellValue;
                return (
                    <Chip
                        label={isVerified ? t("users.yes", "Yes") : t("users.no", "No")}
                        color={isVerified ? "success" : "default"}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        }),
    ] as MRT_ColumnDef<User, string>[];

    // Add dynamic JSON attribute columns
    extendColumnsByJsonAttributes(mrt_columns, userJsonAttributes, users);

    return mrt_columns;
};
